import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { getSupabase } from "../lib/supabase";
import type { Profile } from "../lib/types";

export type AuthStatus = "loading" | "unauthenticated" | "authorized" | "denied";
export type DenyReason = "not_merchant" | "inactive" | "no_membership" | "no_profile" | null;

interface AuthValue {
  status: AuthStatus;
  session: Session | null;
  profile: Profile | null;
  merchantId: string | null;
  merchantName: string | null;
  denyReason: DenyReason;
  signIn: (email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [merchantId, setMerchantId] = useState<string | null>(null);
  const [merchantName, setMerchantName] = useState<string | null>(null);
  const [denyReason, setDenyReason] = useState<DenyReason>(null);
  const loadSeq = useRef(0);
  const settled = useRef(false); // wurde der Auth-Zustand schon einmal aufgelöst?

  const evaluate = useCallback(async (sess: Session | null) => {
    const seq = ++loadSeq.current;
    setSession(sess);
    if (!sess) {
      setProfile(null); setMerchantId(null); setMerchantName(null); setDenyReason(null);
      setStatus("unauthenticated");
      settled.current = true;
      return;
    }
    // Ladezustand NUR beim ersten Auflösen zeigen. Spätere Auth-Events
    // (Token-Refresh, Fokus-Revalidierung durch Supabase) dürfen das
    // Terminal NICHT aus-/wieder einhängen — sonst geht der Alarm-/Audio-
    // Zustand (useAlarm) verloren. Daher Hintergrund-Revalidierung ohne
    // "loading"-Flip. [PWA-VAL-005]
    if (!settled.current) setStatus("loading");
    settled.current = true;
    const sb = getSupabase();
    // Eigenes Profil (RLS: nur eigenes lesbar).
    const { data: prof } = await sb
      .from("profiles")
      .select("id, role, is_active, full_name")
      .eq("id", sess.user.id)
      .maybeSingle();
    if (seq !== loadSeq.current) return; // veraltet

    if (!prof) { setProfile(null); setMerchantId(null); setMerchantName(null); setDenyReason("no_profile"); setStatus("denied"); return; }
    const p = prof as Profile;
    setProfile(p);

    if (p.role !== "merchant") { setMerchantId(null); setMerchantName(null); setDenyReason("not_merchant"); setStatus("denied"); return; }
    if (!p.is_active) { setMerchantId(null); setMerchantName(null); setDenyReason("inactive"); setStatus("denied"); return; }

    // Merchant-Mitgliedschaft serverseitig (my_merchant_ids => SETOF uuid).
    const { data: ids } = await sb.rpc("my_merchant_ids");
    if (seq !== loadSeq.current) return;
    const list = (ids ?? []) as string[];
    if (!list.length) { setMerchantId(null); setMerchantName(null); setDenyReason("no_membership"); setStatus("denied"); return; }

    setMerchantId(list[0]);
    setDenyReason(null);
    setStatus("authorized");

    // Shop-Name aus der bestehenden, autorisierten merchants-View laden
    // (RLS: is_merchant_member — nur der eigene Shop lesbar). Keine neue
    // Policy/kein Grant. Terminal ist bereits gerendert; der Name füllt sich
    // nach. Stale-Guard wie oben. [PWA-VAL-001]
    const { data: m } = await sb
      .from("merchants")
      .select("name")
      .eq("id", list[0])
      .maybeSingle();
    if (seq !== loadSeq.current) return;
    setMerchantName((m as { name?: string | null } | null)?.name ?? null);
  }, []);

  useEffect(() => {
    const sb = getSupabase();
    sb.auth.getSession().then(({ data }) => void evaluate(data.session));
    const { data: sub } = sb.auth.onAuthStateChange((_event, sess) => {
      void evaluate(sess);
    });
    return () => sub.subscription.unsubscribe();
  }, [evaluate]);

  const signIn = useCallback(async (email: string, password: string) => {
    const sb = getSupabase();
    const { error } = await sb.auth.signInWithPassword({ email, password });
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  }, []);

  const signOut = useCallback(async () => {
    const sb = getSupabase();
    await sb.auth.signOut();
    setProfile(null); setMerchantId(null); setMerchantName(null); setDenyReason(null);
  }, []);

  const value = useMemo<AuthValue>(
    () => ({ status, session, profile, merchantId, merchantName, denyReason, signIn, signOut }),
    [status, session, profile, merchantId, merchantName, denyReason, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
